import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, X, Play, ChatText, PlusCircle } from "@phosphor-icons/react";

export default function PbxPhrasebook({ orgId }) {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [edit, setEdit] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["pbx-intents", orgId],
    queryFn: async () => (await api.get("/pbx/intents", { params: orgId ? { org_id: orgId } : {} })).data,
  });
  const { data: extData } = useQuery({
    queryKey: ["pbx-extensions", orgId],
    queryFn: async () => (await api.get("/pbx/extensions", { params: orgId ? { org_id: orgId } : {} })).data,
  });
  const intents = data?.intents || [];
  const exts = extData?.extensions || [];

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/pbx/intents/${id}`)).data,
    onSuccess: () => { toast.success("Intent deleted"); qc.invalidateQueries({ queryKey: ["pbx-intents"] }); },
    onError: (e) => toast.error(apiError(e)),
  });
  const toggle = useMutation({
    mutationFn: async ({ id, enabled }) => (await api.put(`/pbx/intents/${id}`, { enabled })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pbx-intents"] }),
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <div className="space-y-5" data-testid="pbx-phrasebook">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Teach the SI Receptionist your own terminology. Callers can say it their own way — the SI matches meaning, not just keywords.</p>
        <button data-testid="intent-add" onClick={() => { setEdit(null); setShowForm(true); }}
          className="flex shrink-0 items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105">
          <Plus size={16} weight="bold" /> New Intent
        </button>
      </div>

      {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : intents.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 bg-card p-10 text-center">
          <ChatText size={26} className="mx-auto text-muted-foreground" />
          <p className="mt-3 text-sm font-medium">No custom intents yet</p>
          <p className="mt-1 text-xs text-muted-foreground">Add one — e.g. “Policy Renewal” with phrases like “renew my policy”, routing to a Renewals extension.</p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {intents.map((it) => (
            <div key={it.id} data-testid={`intent-${it.id}`} className="rounded-2xl border border-white/10 bg-card p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold">{it.name}</p>
                  <p className="text-xs text-muted-foreground">→ {it.destination_name || it.destination_ext} <span className="font-mono-metric">({it.destination_ext})</span></p>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`intent-toggle-${it.id}`} onClick={() => toggle.mutate({ id: it.id, enabled: !it.enabled })}
                    className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${it.enabled ? "bg-emerald-500/15 text-emerald-500" : "bg-muted text-muted-foreground"}`}>{it.enabled ? "On" : "Off"}</button>
                  <button onClick={() => { setEdit(it); setShowForm(true); }} className="text-xs text-primary hover:underline">Edit</button>
                  <button data-testid={`intent-del-${it.id}`} onClick={() => { if (window.confirm(`Delete intent “${it.name}”?`)) del.mutate(it.id); }} className="text-muted-foreground hover:text-destructive"><Trash size={15} /></button>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(it.phrases || []).slice(0, 8).map((p, i) => (
                  <span key={i} className="rounded-full bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground">“{p}”</span>
                ))}
                {(it.phrases || []).length === 0 && <span className="text-[11px] italic text-muted-foreground">No phrases yet</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      <TestBox orgId={orgId} />

      {showForm && (
        <IntentForm orgId={orgId} exts={exts} intent={edit}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); qc.invalidateQueries({ queryKey: ["pbx-intents"] }); }} />
      )}
    </div>
  );
}

function TestBox({ orgId }) {
  const [input, setInput] = useState("");
  const [result, setResult] = useState(null);
  const run = useMutation({
    mutationFn: async () => (await api.post("/pbx/intents/test", { input, ...(orgId ? { org_id: orgId } : {}) })).data,
    onSuccess: (d) => setResult(d),
    onError: (e) => toast.error(apiError(e)),
  });
  const color = result?.decision === "route" ? "text-emerald-500" : result?.decision === "confirm" ? "text-amber-500" : "text-muted-foreground";
  return (
    <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4" data-testid="intent-test-box">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Test a phrase before publishing</p>
      <div className="mt-2 flex gap-2">
        <input data-testid="intent-test-input" value={input} onChange={(e) => setInput(e.target.value)}
          placeholder='e.g. “my coverage ends next month and I need to continue it”'
          className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
        <button data-testid="intent-test-run" onClick={() => run.mutate()} disabled={!input.trim() || run.isPending}
          className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-40">
          <Play size={15} weight="fill" /> {run.isPending ? "Testing…" : "Test"}</button>
      </div>
      {result && (
        <div className="mt-3 rounded-xl bg-card p-3 text-sm" data-testid="intent-test-result">
          <p className={`font-semibold uppercase ${color}`}>{result.decision}</p>
          {result.ext ? <p className="mt-0.5">→ {result.destination_name || result.ext} <span className="font-mono-metric">({result.ext})</span>{result.intent ? ` · intent “${result.intent}”` : ""}</p> : <p className="mt-0.5 text-muted-foreground">No confident destination — would ask a clarifying question.</p>}
          <p className="mt-1 text-xs text-muted-foreground">Confidence {Math.round((result.confidence || 0) * 100)}% · method {result.method || "—"}</p>
          {result.say && <p className="mt-1 text-xs italic text-muted-foreground">SI would say: “{result.say}”</p>}
        </div>
      )}
    </div>
  );
}

function IntentForm({ orgId, exts, intent, onClose, onSaved }) {
  const [name, setName] = useState(intent?.name || "");
  const [dest, setDest] = useState(intent?.destination_ext || (exts[0]?.ext || ""));
  const [phrases, setPhrases] = useState(intent?.phrases?.length ? [...intent.phrases] : [""]);
  const [alwaysConfirm, setAlwaysConfirm] = useState(!!intent?.always_confirm);
  const [minConf, setMinConf] = useState(intent?.min_confidence ?? "");
  const setPhrase = (i, v) => setPhrases((p) => p.map((x, j) => (j === i ? v : x)));

  const save = useMutation({
    mutationFn: async () => {
      const body = { name, destination_ext: dest, phrases: phrases.filter((p) => p.trim()),
        always_confirm: alwaysConfirm, min_confidence: minConf === "" ? null : Number(minConf) };
      if (intent) return (await api.put(`/pbx/intents/${intent.id}`, body)).data;
      return (await api.post("/pbx/intents", { ...body, org_id: orgId || undefined })).data;
    },
    onSuccess: () => { toast.success(intent ? "Intent updated" : "Intent published"); onSaved(); },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="intent-form-modal">
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">{intent ? "Edit Intent" : "New Intent"}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <label className="block text-xs font-semibold text-muted-foreground">Intent name
          <input data-testid="intent-name" value={name} onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-lg border border-white/10 bg-background px-3 py-2 text-sm" placeholder="Policy Renewal" />
        </label>
        <label className="mt-3 block text-xs font-semibold text-muted-foreground">Route to extension
          <select data-testid="intent-dest" value={dest} onChange={(e) => setDest(e.target.value)}
            className="mt-1 w-full rounded-lg border border-white/10 bg-background px-3 py-2 text-sm">
            {exts.map((e) => <option key={e.ext} value={e.ext}>{e.ext} — {e.name}</option>)}
          </select>
        </label>
        <div className="mt-3">
          <p className="text-xs font-semibold text-muted-foreground">Example phrases</p>
          <div className="mt-1 space-y-2">
            {phrases.map((p, i) => (
              <div key={i} className="flex gap-2">
                <input data-testid={`intent-phrase-${i}`} value={p} onChange={(e) => setPhrase(i, e.target.value)}
                  className="flex-1 rounded-lg border border-white/10 bg-background px-3 py-2 text-sm" placeholder="e.g. renew my policy" />
                <button onClick={() => setPhrases((ps) => ps.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive"><Trash size={15} /></button>
              </div>
            ))}
          </div>
          <button data-testid="intent-add-phrase" onClick={() => setPhrases((p) => [...p, ""])}
            className="mt-2 flex items-center gap-1 text-xs font-semibold text-primary hover:underline"><PlusCircle size={14} /> Add phrase</button>
        </div>
        <div className="mt-3 rounded-xl border border-white/10 bg-background p-3">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input data-testid="intent-always-confirm" type="checkbox" checked={alwaysConfirm} onChange={(e) => setAlwaysConfirm(e.target.checked)} className="h-4 w-4" />
            Always confirm before transferring (for sensitive routes)
          </label>
          <label className="mt-2 block text-xs font-semibold text-muted-foreground">Minimum confidence to route (optional, 0–1)
            <input data-testid="intent-min-conf" value={minConf} onChange={(e) => setMinConf(e.target.value.replace(/[^0-9.]/g, ""))}
              placeholder="e.g. 0.85" className="mt-1 w-full rounded-lg border border-white/10 bg-card px-3 py-2 text-sm" />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full px-4 py-2 text-sm font-semibold text-muted-foreground hover:text-foreground">Cancel</button>
          <button data-testid="intent-save" onClick={() => save.mutate()} disabled={save.isPending || !name.trim() || !dest}
            className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-40">
            {save.isPending ? "Saving…" : intent ? "Save changes" : "Save & Publish"}</button>
        </div>
      </div>
    </div>
  );
}
